import { NARRATORS, type Narrator } from "../narrators";

export const SettingKey = {
  // Appearance settings
  THEME: "theme",
  NARRATOR: "narrator",

  // Email preferences
  EMAIL_FEED_UPDATES: "email_feed_updates",
  EMAIL_RECOMMENDATIONS: "email_recommendations",
  EMAIL_PRODUCT_ANNOUNCEMENTS: "email_product_announcements",
  EMAIL_MARKETING: "email_marketing",

  // Audio settings
  AUDIO_SPEED: "audio_speed",

  // Reading preferences
  SUMMARY_EXPANDED: "summary_expanded",
} as const;

export type SettingKey = (typeof SettingKey)[keyof typeof SettingKey];

export type Theme = "light" | "dark" | "system";
export type EmailFrequency = "weekly" | "monthly" | "never";
export type OnOffSetting = "on" | "off";
export type AudioSpeed = "0.75" | "1" | "1.25" | "1.5" | "2";

export type UserSettings = {
  theme?: Theme;
  narrator?: Narrator;
  email_feed_updates?: EmailFrequency;
  email_recommendations?: EmailFrequency;
  email_product_announcements?: OnOffSetting;
  email_marketing?: OnOffSetting;
  audio_speed?: AudioSpeed;
  summary_expanded?: OnOffSetting;
};

export type UserSetting = {
  key: SettingKey;
  value: string;
};

/** Valid values for each setting key. */
export const SETTING_VALUE_CONSTRAINTS: Record<SettingKey, readonly string[]> = {
  [SettingKey.THEME]: ["system", "light", "dark"],
  [SettingKey.NARRATOR]: NARRATORS,
  [SettingKey.EMAIL_FEED_UPDATES]: ["weekly", "monthly", "never"],
  [SettingKey.EMAIL_RECOMMENDATIONS]: ["weekly", "monthly", "never"],
  [SettingKey.EMAIL_PRODUCT_ANNOUNCEMENTS]: ["on", "off"],
  [SettingKey.EMAIL_MARKETING]: ["on", "off"],
  [SettingKey.AUDIO_SPEED]: ["0.75", "1", "1.25", "1.5", "2"],
  [SettingKey.SUMMARY_EXPANDED]: ["on", "off"],
};

/** Every optional email is off until the user turns it on (opt-in); users from before keep the rows written at their sign-up. */
export const DEFAULT_SETTINGS: Record<SettingKey, string> = {
  [SettingKey.THEME]: "system",
  [SettingKey.NARRATOR]: "af",
  [SettingKey.EMAIL_FEED_UPDATES]: "never",
  [SettingKey.EMAIL_RECOMMENDATIONS]: "never",
  [SettingKey.EMAIL_PRODUCT_ANNOUNCEMENTS]: "off",
  [SettingKey.EMAIL_MARKETING]: "off",
  [SettingKey.AUDIO_SPEED]: "1",
  [SettingKey.SUMMARY_EXPANDED]: "off",
};

export const SETTING_KEYS: SettingKey[] = Object.values(SettingKey);

export const isSettingKey = (key: string): key is SettingKey => {
  return (SETTING_KEYS as string[]).includes(key);
};

export const isValidSettingValue = (key: SettingKey, value: string): boolean => {
  return SETTING_VALUE_CONSTRAINTS[key].includes(value);
};

// User tips are dismissable hints/tooltips (not to be confused with the money tips in `tips`).
export const TipKey = {
  PLAYLIST_MODE: "playlist_mode",
} as const;

export type TipKey = (typeof TipKey)[keyof typeof TipKey];

export type UserTips = Partial<Record<TipKey, boolean>>;
